import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { runJourneyCommand } from './journeyOperations';
import type { JourneyCommand } from './journeyOperations';
import { legacyDelete } from './queries';
import { assertNoPendingLegacyJourney } from './journeyPending';
import { trackerKeys } from './hooks';
import { activityKeys } from './leadActivity';
import type { LeadActivity, LeadActivityKind } from './leadActivity';
import type { SupabaseClient } from '../../lib/supabase';
import type { Database, Tables } from '../../types/database.types';
import { batchKeys, findOrCreateBatch, planBatches } from './prospectBatches';
import type { BatchSource } from './prospectBatches';

/**
 * Prospects: stage zero of the funnel (20270121000100).
 *
 * People Senku found and Hanneza is chasing on LinkedIn. Not leads — nothing
 * that reads `leads` sees these — and they become leads only through the
 * promotion RPC, one stage earlier than `promote_lead_to_deal`.
 *
 * Every write that records WORK goes through an RPC, because the status and
 * the activity row have to land together: Connected is a `linkedin_added`
 * activity, Pitched is a `linkedin` one. Editing the row's plain fields (a
 * corrected title, a found email) is an ordinary update and logs nothing.
 */

export type Prospect = Tables<'prospects'>;

export type ProspectTier = 'tier1' | 'tier2' | 'trash';

export const PROSPECT_TIERS: { key: ProspectTier; label: string }[] = [
  { key: 'tier1', label: 'Tier 1' },
  { key: 'tier2', label: 'Tier 2' },
  { key: 'trash', label: 'Trash' },
];

export function isProspectTier(value: string): value is ProspectTier {
  return PROSPECT_TIERS.some((entry) => entry.key === value);
}

export interface SetProspectTierResult {
  changed: number;
  total: number;
  error: string;
}

function tierError(message: string): string {
  return message.includes('prospects_tier_check')
    ? 'Choose Tier 1, Tier 2, Trash, or Clear.'
    : prospectErrorMessage(message);
}

/** Triage only: count returned IDs under RLS, without recording outreach. */
export async function setProspectTier(
  client: SupabaseClient,
  ids: readonly string[],
  tier: ProspectTier | null,
): Promise<SetProspectTierResult> {
  const uniqueIds = [...new Set(ids)];
  const total = uniqueIds.length;
  let changed = 0;
  for (let offset = 0; offset < total; offset += 100) {
    try {
      const { data, error } = await client
        .from('prospects')
        .update({ tier })
        .in('id', uniqueIds.slice(offset, offset + 100))
        .select('id');
      if (error) return { changed, total, error: tierError(error.message) };
      changed += Array.isArray(data) ? data.length : 0;
    } catch (cause) {
      return { changed, total, error: tierError(cause instanceof Error ? cause.message : '') };
    }
  }
  return { changed, total, error: '' };
}

export function tierResultMessage(result: SetProspectTierResult): string {
  return `Updated tier for ${String(result.changed)} of ${String(result.total)} prospects.${result.changed === result.total ? '' : ' The rest were not updated.'}${result.error === '' ? '' : ` ${result.error}`}`;
}

export function useSetProspectTier() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; tier: ProspectTier | null }) =>
      setProspectTier(client, input.ids, input.tier),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
    },
  });
}

export type ProspectStatus =
  'new' | 'pending' | 'connected' | 'pitched' | 'dm_2' | 'dm_3' | 'left_company' | 'cant_connect';

/**
 * In dropdown order: the path forward first, then the two dead ends.
 *
 * New and Pending are two different afternoons, which is why both exist
 * (20270125000100). New means nobody has done anything. Pending means the
 * connection request is out and we are waiting on them.
 *
 * The three DMs are the funnel Kyu set on 17 September (20270150000100):
 * New › Request sent › Connected › 1st DM › 2nd DM › 3rd DM › Promoted.
 * `pitched` is the stored value for the 1st DM and keeps it, the same way
 * `pending` kept its value when its label became "Request sent". Skipping
 * is allowed: Request sent straight to Promote is one click and always was.
 */
export const PROSPECT_STATUSES: { key: ProspectStatus; label: string }[] = [
  { key: 'new', label: 'New' },
  // "Request sent", not "Pending". The stored value is still `pending` and
  // means what 20270125000100 made it mean: the connection request is out and
  // we are waiting. The WORD was the problem. On 10 September Hanneza set nine
  // people to Pending within the hour, reading it as "nothing done yet" — the
  // meaning it carried until that morning — while the Hub logged each one as
  // "Connection request sent" in her name. A label that disagrees with the
  // sentence it writes into the timeline is a label that will be misread
  // again.
  { key: 'pending', label: 'Request sent' },
  { key: 'connected', label: 'Connected' },
  { key: 'pitched', label: '1st DM' },
  { key: 'dm_2', label: '2nd DM' },
  { key: 'dm_3', label: '3rd DM' },
  { key: 'left_company', label: 'Left company' },
  { key: 'cant_connect', label: "Can't connect" },
];

const STATUS_LABELS = new Map(PROSPECT_STATUSES.map((entry) => [entry.key, entry.label]));

export function prospectStatusLabel(status: string): string {
  return STATUS_LABELS.get(status as ProspectStatus) ?? status;
}

export function isProspectStatus(value: string): value is ProspectStatus {
  return STATUS_LABELS.has(value as ProspectStatus);
}

/** The two statuses that mean the outreach is over without a conversation. */
export function isDeadEnd(status: string): boolean {
  return status === 'left_company' || status === 'cant_connect';
}

/**
 * How far along the path a status is. The funnel counts "reached or passed"
 * from this, so a prospect who jumped from Request sent to Promoted counts
 * in Request sent and Promoted and not in between, and a later stage can
 * never read larger than an earlier one. Dead ends have no rank: they left.
 */
export const PROSPECT_STAGE_RANK: Record<ProspectStatus, number | null> = {
  new: 0,
  pending: 1,
  connected: 2,
  pitched: 3,
  dm_2: 4,
  dm_3: 5,
  left_company: null,
  cant_connect: null,
};

export function prospectStageRank(status: string): number | null {
  return isProspectStatus(status) ? PROSPECT_STAGE_RANK[status] : null;
}

export function prospectName(
  prospect: Pick<Prospect, 'first_name' | 'last_name' | 'email'>,
): string {
  const name = `${prospect.first_name} ${prospect.last_name}`.trim();
  return name === '' ? prospect.email || 'Unnamed prospect' : name;
}

export const prospectKeys = {
  all: () => [...trackerKeys.all, 'prospects'] as const,
  activity: (prospectId: string) =>
    [...trackerKeys.all, 'prospects', 'activity', prospectId] as const,
};

function fail(message: string): never {
  throw new Error(message);
}

const PROSPECT_COLUMNS =
  'id, first_name, last_name, email, phone, title, company_name, company_id, batch_id, linkedin_url, why_relevant, opening_hook, status, status_since, tier, source, sourced_on, promoted_lead_id, promoted_at, created_by, created_at, updated_at';

/**
 * Every prospect, in pages, because asking for all of them does not return
 * all of them.
 *
 * This used to be one request with `.limit(2000)`. Supabase's API caps every
 * response at its server `max_rows`, 1000 by default, and a larger limit is
 * silently clamped to it: no error, no header anyone reads, just the newest
 * 1000 rows. That was invisible until 21 September, when Alex's connections
 * import took the table past 1000 and the Prospecting tab, the funnel counts
 * and the import drawer's duplicate check all quietly started seeing only
 * the newest 1000 people.
 *
 * So this walks the table with `.range()`, advancing by however many rows
 * each page actually returned, and stops at the first empty page. Advancing
 * by the returned count rather than an assumed page size means a server cap
 * lower than PROSPECT_PAGE still reads everything instead of stopping early.
 * `id` is the tie-breaker so rows created in the same instant cannot slip
 * between two pages.
 */
export const PROSPECT_PAGE = 1000;
const PROSPECT_PAGE_LIMIT = 100;

export async function fetchProspects(client: SupabaseClient): Promise<Prospect[]> {
  const everyone: Prospect[] = [];
  for (let page = 0; page < PROSPECT_PAGE_LIMIT; page += 1) {
    const from = everyone.length;
    const { data, error } = await client
      .from('prospects')
      .select(PROSPECT_COLUMNS)
      .order('created_at', { ascending: false })
      .order('id', { ascending: true })
      .range(from, from + PROSPECT_PAGE - 1);
    if (error) fail(error.message);
    const rows = Array.isArray(data) ? (data as Prospect[]) : [];
    if (rows.length === 0) return everyone;
    everyone.push(...rows);
  }
  // A hundred pages is 100,000 prospects. Past that, failing loudly beats
  // quietly returning a partial list, which is the bug this replaced.
  fail(`More than ${String(PROSPECT_PAGE * PROSPECT_PAGE_LIMIT)} prospects; paging stopped.`);
}

export function useProspects() {
  const { client } = useAuth();
  return useQuery({
    queryKey: prospectKeys.all(),
    queryFn: () => fetchProspects(client),
    retry: false,
  });
}

const ACTIVITY_COLUMNS =
  'id, lead_id, prospect_id, kind, body, occurred_at, occurred_is_day, source, list_id, copy_set_id, message_slot, message_selected_at, message_version, round_id, touch_number, actor_id, external_id, created_at';

export async function fetchProspectActivity(
  client: SupabaseClient,
  prospectId: string,
): Promise<LeadActivity[]> {
  const { data, error } = await client
    .from('lead_activity')
    .select(ACTIVITY_COLUMNS)
    .eq('prospect_id', prospectId)
    .order('occurred_at', { ascending: false });
  if (error) fail(error.message);
  return Array.isArray(data) ? data : [];
}

export function useProspectActivity(prospectId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: prospectKeys.activity(prospectId),
    queryFn: () => fetchProspectActivity(client, prospectId),
    enabled: prospectId !== '',
  });
}

/** What a new prospect row carries. Everything optional but the name. */
export interface ProspectInput {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
  company_name: string;
  linkedin_url: string;
  why_relevant: string;
  opening_hook: string;
  status: ProspectStatus;
  /** How this row got in. 20270152000100 has the four, and why. */
  source: 'senku_daily' | 'linkedin_connections' | 'old_tracker' | 'manual';
  /**
   * The day this person entered our world: research found them, or they
   * accepted the request, depending on the source. Null when nobody said.
   * See 20270123000100 and importSourceDateMeaning.
   */
  sourced_on: string | null;
}

export interface AddProspectsResult {
  added: number;
  skipped: number;
  failed: number;
  failures: string[];
  /** Batch plans, newest month first in Auto mode. */
  batches: { name: string; count: number }[];
}

export function useAddProspects() {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = access.status === 'approved' ? access.profile.id : null;
  return useMutation({
    mutationFn: async (input: {
      rows: ProspectInput[];
      /** Omit to file nothing unless a batch is explicitly chosen. */
      batchSource?: BatchSource;
      batchId?: string | null;
      newBatchName?: string;
    }): Promise<AddProspectsResult> => {
      const { rows } = input;
      if (input.newBatchName !== undefined && input.newBatchName.trim() === '') {
        fail('Give the batch a name.');
      }
      const result: AddProspectsResult = {
        added: 0,
        skipped: 0,
        failed: 0,
        failures: [],
        batches: [],
      };
      if (rows.length === 0) return result;

      // Resolve batches before rows. A failed import can leave a visible,
      // harmless empty batch; no row should point at a batch not yet made.
      const batchIdByRow = new Map<number, string>();
      if (input.newBatchName !== undefined) {
        const batch = await findOrCreateBatch(client, {
          name: input.newBatchName,
          source: input.batchSource ?? rows[0]?.source ?? 'manual',
          createdBy,
        });
        rows.forEach((_, index) => batchIdByRow.set(index, batch.id));
        result.batches.push({ name: batch.name, count: rows.length });
      } else if (input.batchId !== undefined) {
        if (input.batchId !== null) {
          const batchId = input.batchId;
          rows.forEach((_, index) => batchIdByRow.set(index, batchId));
        }
      } else {
        const planned = input.batchSource === undefined ? [] : planBatches(rows, input.batchSource);
        for (const plan of planned) {
          const batch = await findOrCreateBatch(client, {
            name: plan.name,
            source: plan.source,
            createdBy,
          });
          for (const index of plan.rows) batchIdByRow.set(index, batch.id);
          result.batches.push({ name: batch.name, count: plan.rows.length });
        }
      }

      // Map before slicing: fallback rows keep their ORIGINAL batch indexes.
      const payload = rows.map((row, index) => ({
        ...row,
        created_by: createdBy,
        batch_id: batchIdByRow.get(index) ?? null,
      }));
      for (let offset = 0; offset < payload.length; offset += 50) {
        const chunk = payload.slice(offset, offset + 50);
        const { data, error } = await client.from('prospects').insert(chunk).select('id');
        if (!error) {
          result.added += Array.isArray(data) ? data.length : 0;
          continue;
        }
        // A failed multi-row statement writes nothing. Retry its rows only.
        for (const row of chunk) {
          const { data: inserted, error: rowError } = await client
            .from('prospects')
            .insert([row])
            .select('id');
          if (rowError?.code === '23505') result.skipped += 1;
          else if (rowError) {
            result.failed += 1;
            result.failures.push(`${prospectName(row)}: ${rowError.message}`);
          } else result.added += Array.isArray(inserted) ? inserted.length : 0;
        }
      }
      return result;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
      void queryClient.invalidateQueries({ queryKey: batchKeys.all() });
    },
  });
}

export type ProspectChanges = Partial<
  Pick<
    Prospect,
    | 'first_name'
    | 'last_name'
    | 'email'
    | 'phone'
    | 'title'
    | 'company_name'
    | 'linkedin_url'
    | 'why_relevant'
    | 'opening_hook'
  >
>;

/** Plain fields only. Status goes through useSetProspectStatus so the work is logged. */
export function useUpdateProspect() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      changes,
    }: {
      id: string;
      changes: ProspectChanges;
    }): Promise<void> => {
      const { error } = await client.from('prospects').update(changes).eq('id', id);
      if (error) fail(error.message);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
    },
  });
}

export function useDeleteProspect() {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (command: JourneyCommand | string): Promise<void> => {
      if (typeof command === 'string') {
        assertNoPendingLegacyJourney(access.status === 'approved' ? access.profile.id : '');
        return legacyDelete(client, 'prospects', command);
      }
      if (command.kind !== 'cleanup' || command.table !== 'prospects')
        fail('journey_command_mismatch');
      await runJourneyCommand(client, command);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
    },
  });
}

/**
 * The status dropdown. One RPC: the status moves and the activity that
 * records the work lands in the same transaction, or neither does.
 */
export function useSetProspectStatus() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      prospectId: string;
      status: ProspectStatus;
      /** A calendar day, `YYYY-MM-DD`. Omit for "now". */
      occurredOn?: string;
    }): Promise<void> => {
      const { error } = await client.rpc('set_prospect_status', {
        p_prospect: input.prospectId,
        p_status: input.status,
        ...(input.occurredOn === undefined ? {} : { p_occurred_on: input.occurredOn }),
      });
      if (error) fail(error.message);
    },
    // The row shows the new status the moment it's picked. Waiting for the
    // refetch meant reloading every prospect (two pages at ~1900) before the
    // dropdown moved, and it sat blank for ~7 seconds (Hanneza, 22 Sept).
    onMutate: async (input) => {
      const key = prospectKeys.all();
      await queryClient.cancelQueries({ queryKey: key, exact: true });
      const rows = queryClient.getQueryData<Prospect[]>(key);
      const before = rows?.find((row) => row.id === input.prospectId)?.status;
      if (rows !== undefined && before !== undefined) {
        queryClient.setQueryData<Prospect[]>(
          key,
          rows.map((row) => (row.id === input.prospectId ? { ...row, status: input.status } : row)),
        );
      }
      return { before };
    },
    // Undo only this row, and only if nothing has moved it since: restoring
    // the whole list would throw away another row's change made meanwhile.
    onError: (_error, input, context) => {
      const before = context?.before;
      if (before === undefined) return;
      queryClient.setQueryData<Prospect[]>(prospectKeys.all(), (rows) =>
        rows?.map((row) =>
          row.id === input.prospectId && row.status === input.status
            ? { ...row, status: before }
            : row,
        ),
      );
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
      void queryClient.invalidateQueries({ queryKey: prospectKeys.activity(input.prospectId) });
    },
  });
}

export function useLogProspectActivity() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      prospectId: string;
      kind: LeadActivityKind;
      body?: string;
      occurredOn?: string;
    }): Promise<void> => {
      const { error } = await client.rpc('log_prospect_activity', {
        p_prospect: input.prospectId,
        p_kind: input.kind,
        p_body: input.body ?? '',
        ...(input.occurredOn === undefined ? {} : { p_occurred_on: input.occurredOn }),
      });
      if (error) fail(error.message);
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.activity(input.prospectId) });
    },
  });
}

/**
 * Promote to Lead. Returns the lead id so the screen can open it.
 *
 * `leadId` attaches to an existing lead instead of creating one — the answer
 * to `lead_email_exists`, which the database raises rather than guessing when
 * the prospect's email already belongs to a lead.
 */
export function usePromoteProspect() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { prospectId: string; leadId?: string }): Promise<string> => {
      const { data, error } = await client.rpc('promote_prospect_to_lead', {
        p_prospect: input.prospectId,
        ...(input.leadId === undefined ? {} : { p_lead: input.leadId }),
      });
      if (error) fail(error.message);
      return typeof data === 'string' ? data : '';
    },
    onSettled: (leadId, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
      void queryClient.invalidateQueries({ queryKey: prospectKeys.activity(input.prospectId) });
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
      if (typeof leadId === 'string' && leadId !== '') {
        void queryClient.invalidateQueries({ queryKey: activityKeys.forLead(leadId) });
      }
    },
  });
}

/** The lead an email already belongs to, for the attach offer. */
export async function findLeadByEmail(
  client: SupabaseClient,
  email: string,
): Promise<{ id: string; first_name: string; last_name: string } | null> {
  const { data, error } = await client
    .from('leads')
    .select('id, first_name, last_name')
    .eq('email', email.trim())
    .limit(1);
  if (error) fail(error.message);
  const row = Array.isArray(data) ? data[0] : undefined;
  return row ?? null;
}

/** The database's refusals, in words a person can act on. */
export function prospectErrorMessage(message: string): string {
  if (message.includes('lead_email_exists')) {
    return 'A lead with this email already exists.';
  }
  if (message.includes('prospect_already_promoted')) {
    return 'This prospect has already been promoted. Work on the lead instead.';
  }
  if (message.includes('prospects_linkedin_unique')) {
    return 'A prospect with this LinkedIn URL already exists.';
  }
  if (message.includes('prospects_email_unique')) {
    return 'A prospect with this email already exists.';
  }
  if (message.includes('unknown_dm_number')) return 'Choose DM 1, 2, or 3.';
  if (message.includes('prospect_not_found'))
    return 'This prospect could not be found. Refresh the list.';
  if (message.includes('copy_set_not_available'))
    return 'That message is unavailable. Choose an active LinkedIn message.';
  if (message.includes('unknown_milestone_kind'))
    return 'Choose Replied, Info reply, or Overview sent.';
  if (message.includes('activity_in_the_future')) {
    return 'That date is in the future.';
  }
  if (message.includes('not_a_sales_member')) {
    return 'Only sales members can do that.';
  }
  return 'That change could not be saved. Try again.';
}

export type ProspectFunnelRow = Database['public']['Views']['prospect_dm_funnel']['Row'];
export type ProspectMilestoneKind = 'replied' | 'info_reply' | 'overview_sent';
const FUNNEL_COLUMNS =
  'prospect_id, tier, status, source, batch_id, promoted_at, promoted_lead_id, first_dm_at, last_dm_at, dm_count, first_dm_copy_set_id, replied_at, replied_copy_set_id, info_reply_at, overview_sent_at, call_booked_at';
const dmKeys = {
  funnel: () => [...prospectKeys.all(), 'dm-funnel'] as const,
  cap: () => [...prospectKeys.all(), 'dm-cap'] as const,
  messages: () => [...prospectKeys.all(), 'dm-messages'] as const,
};

/** Advance by the returned count, including when the API cap is below 1000. */
export async function fetchProspectFunnelRows(
  client: SupabaseClient,
): Promise<ProspectFunnelRow[]> {
  const everyone: ProspectFunnelRow[] = [];
  for (let page = 0; page < PROSPECT_PAGE_LIMIT; page += 1) {
    const from = everyone.length;
    const { data, error } = await client
      .from('prospect_dm_funnel')
      .select(FUNNEL_COLUMNS)
      .order('prospect_id', { ascending: true })
      .range(from, from + PROSPECT_PAGE - 1);
    if (error) fail(error.message);
    const rows = Array.isArray(data) ? data : [];
    if (rows.length === 0) return everyone;
    everyone.push(...rows);
  }
  fail('Too many prospect funnel rows. Paging stopped.');
}

export function useProspectFunnelRows() {
  const { client } = useAuth();
  return useQuery({
    queryKey: dmKeys.funnel(),
    queryFn: () => fetchProspectFunnelRows(client),
    retry: false,
  });
}

/** Event counts keep every DM, including the history moved onto a lead. */
export async function fetchDmMessageCounts(client: SupabaseClient): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  let from = 0;
  for (let page = 0; page < PROSPECT_PAGE_LIMIT; page += 1) {
    const { data, error } = await client
      .from('lead_activity')
      .select('id, copy_set_id')
      .eq('kind', 'linkedin')
      .order('id', { ascending: true })
      .range(from, from + PROSPECT_PAGE - 1);
    if (error) fail(error.message);
    const rows = Array.isArray(data) ? data : [];
    if (rows.length === 0) return counts;
    for (const row of rows)
      if (row.copy_set_id !== null)
        counts.set(row.copy_set_id, (counts.get(row.copy_set_id) ?? 0) + 1);
    from += rows.length;
  }
  fail('Too many DM events. Paging stopped.');
}

export function useDmMessageCounts() {
  const { client } = useAuth();
  return useQuery({
    queryKey: dmKeys.messages(),
    queryFn: () => fetchDmMessageCounts(client),
    retry: false,
  });
}

export function useDmCapStatus() {
  const { client } = useAuth();
  return useQuery({
    queryKey: dmKeys.cap(),
    retry: false,
    refetchInterval: 60000,
    queryFn: async () => {
      const { data, error } = await client.rpc('dm_daily_cap_status');
      if (error) fail(error.message);
      const row = data?.[0];
      if (row === undefined) fail('Daily cap status unavailable.');
      return row;
    },
  });
}

function useInvalidateDmEvidence() {
  const queryClient = useQueryClient();
  return (prospectId: string, leadId?: string) => {
    void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
    void queryClient.invalidateQueries({ queryKey: prospectKeys.activity(prospectId) });
    void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    if (leadId !== undefined && leadId !== '')
      void queryClient.invalidateQueries({ queryKey: activityKeys.forLead(leadId) });
  };
}

export function useLogProspectDm() {
  const { client } = useAuth();
  const invalidate = useInvalidateDmEvidence();
  return useMutation({
    mutationFn: async (input: {
      prospectId: string;
      dmNumber: number;
      copySetId: string | null;
      occurredOn?: string;
    }): Promise<string> => {
      const { data, error } = await client.rpc('log_prospect_dm', {
        p_prospect: input.prospectId,
        p_dm_number: input.dmNumber,
        ...(input.copySetId === null ? {} : { p_copy_set: input.copySetId }),
        ...(input.occurredOn === undefined ? {} : { p_occurred_on: input.occurredOn }),
      });
      if (error) fail(error.message);
      if (typeof data !== 'string' || data === '')
        fail('The DM could not be confirmed. Reload before logging again.');
      return data;
    },
    onSettled: (_data, _error, input) => invalidate(input.prospectId),
  });
}

export function useRecordProspectMilestone() {
  const { client } = useAuth();
  const invalidate = useInvalidateDmEvidence();
  return useMutation({
    mutationFn: async (input: {
      prospectId: string;
      kind: ProspectMilestoneKind;
      occurredOn?: string;
    }): Promise<void> => {
      const { error } = await client.rpc('record_prospect_milestone', {
        p_prospect: input.prospectId,
        p_kind: input.kind,
        ...(input.occurredOn === undefined ? {} : { p_occurred_on: input.occurredOn }),
      });
      if (error) fail(error.message);
    },
    onSettled: (_data, _error, input) => invalidate(input.prospectId),
  });
}

export function useBookCallForProspect() {
  const { client } = useAuth();
  const invalidate = useInvalidateDmEvidence();
  return useMutation({
    mutationFn: async (input: {
      prospectId: string;
      leadId?: string;
      occurredOn?: string;
    }): Promise<string> => {
      const { data, error } = await client.rpc('book_call_for_prospect', {
        p_prospect: input.prospectId,
        ...(input.leadId === undefined ? {} : { p_lead: input.leadId }),
        ...(input.occurredOn === undefined ? {} : { p_occurred_on: input.occurredOn }),
      });
      if (error) fail(error.message);
      return typeof data === 'string' ? data : '';
    },
    onSettled: (leadId, _error, input) => invalidate(input.prospectId, leadId),
  });
}
